import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, fmtDate, fmtINR } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Download, MapPin, Calendar, AlertCircle, MessageSquare } from "lucide-react";

export default function Booking() {
  const { ref } = useParams();
  const [b, setB] = useState(null);
  const [event, setEvent] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${ref}`).then(async (r) => {
      setB(r.data);
      const ev = await api.get(`/events/${r.data.event_slug}`).catch(() => null);
      setEvent(ev?.data);
    });
  }, [ref]);

  if (!b) return <PublicLayout><div className="p-10 text-center">Loading...</div></PublicLayout>;

  // A booking is confirmed if paid, free_confirmed, or has a recorded razorpay_payment_id
  const confirmed = b.status === "paid" || b.status === "free_confirmed" || Boolean(b.razorpay_payment_id);
  const isExpired = !confirmed && b.status === "expired";

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        
        {/* Expired Warning Banner */}
        {isExpired && (
          <div className="mb-6 p-4 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 text-sm">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-sm">Reservation Window Expired</h3>
                <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                  If payment was deducted from your UPI or bank account, <b>your money is completely safe!</b> Tap the button below to message our team on WhatsApp with your reference <b>{b.reference}</b>, and we will activate your passes immediately.
                </p>
                <div className="mt-3">
                  <a
                    href={`https://wa.me/918247820881?text=${encodeURIComponent(
                      `Hi CGC Team, I completed payment on Razorpay/UPI for booking reference ${b.reference} (${b.event_title}), but my pass is showing expired. Please help activate my pass!`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-green-600 hover:bg-green-700 text-white"
                  >
                    <MessageSquare size={13} />
                    <span>Verify & Activate on WhatsApp</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Booking Header Card */}
        <div className={`cgc-card p-6 flex items-start gap-4 ${confirmed ? "border-green-500/40" : isExpired ? "border-amber-500/40" : ""}`}>
          <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
            confirmed ? "bg-green-100 dark:bg-green-950 text-green-700" : "bg-amber-100 dark:bg-amber-950 text-amber-700"
          }`}>
            {confirmed ? <CheckCircle2 size={24} /> : <AlertCircle size={24} />}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-mono uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                confirmed ? "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300" : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
              }`}>
                {confirmed ? "Confirmed Pass" : b.status}
              </span>
              {b.organizer_slug && b.organizer_slug !== "cgc" && (
                <span className="text-[10px] font-mono text-stone-500">
                  via {b.organizer_slug}
                </span>
              )}
            </div>

            <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1.5">{b.event_title}</h1>
            
            <div className="mt-2.5 text-xs text-stone-600 dark:text-stone-400 flex flex-wrap items-center gap-x-5 gap-y-1.5">
              {event && (
                <>
                  <span className="flex items-center gap-1.5 font-medium text-stone-800 dark:text-stone-200">
                    <Calendar size={13} className="text-orange-600" />
                    <span>Event Date: <b>{fmtDate(event.start_at)}</b></span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <MapPin size={13} className="text-orange-600" />
                    <span>Venue: {event.venue_name || "Online"}</span>
                  </span>
                </>
              )}
              <span className="font-mono">Ref: <b data-testid="booking-ref">{b.reference}</b></span>
            </div>
          </div>

          <div className="text-right shrink-0">
            <div className="text-xs text-stone-500">{confirmed ? "Total Paid" : "Payable Amount"}</div>
            <div className="font-display text-xl sm:text-2xl font-bold text-stone-900 dark:text-stone-100">
              {fmtINR(b.total_paise)}
            </div>
          </div>
        </div>

        {/* Tickets Section */}
        <h2 className="font-display text-2xl font-bold mt-10 mb-4">
          {confirmed ? "Your tickets" : "Registered Attendees"}
        </h2>

        {!confirmed && (
          <div className="p-4 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 text-sm mb-6">
            <div className="flex items-center gap-2 font-bold">
              <AlertCircle size={18} className="text-amber-600 shrink-0" />
              <span>Passes Locked — Payment Incomplete</span>
            </div>
            <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
              Official venue check-in QR passes and PDF ticket downloads are only activated and issued after payment is confirmed.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {b.attendees.map((a, idx) => (
            <div key={a.ticket_id || idx} className="cgc-card p-5 flex flex-col sm:flex-row gap-6" data-testid={`ticket-${a.ticket_id || idx}`}>
              <div className="flex-1">
                <div className="eyebrow">{a.tier_name}</div>
                <div className="font-display text-xl font-semibold mt-1">{a.name}</div>
                <div className="text-sm text-stone-500">{a.email}{a.phone ? ` • ${a.phone}` : ""}</div>
                {confirmed && a.ticket_id ? (
                  <>
                    <div className="mt-4 text-xs font-mono text-stone-500">Ticket ID: {a.ticket_id}</div>
                    <div className="mt-4">
                      <a href={`${API}/tickets/${a.ticket_id}/pdf`} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" data-testid={`ticket-pdf-${a.ticket_id}`}>
                          <Download size={14} className="mr-1.5" />Download PDF
                        </Button>
                      </a>
                    </div>
                  </>
                ) : (
                  <div className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-stone-100 dark:bg-stone-800 text-xs font-mono text-stone-600 dark:text-stone-400">
                    Pass Status: <span className="font-semibold text-amber-600 uppercase">{b.status}</span> (Unconfirmed)
                  </div>
                )}
              </div>
              {confirmed && a.ticket_id ? (
                <div className="flex flex-col items-center gap-2 border border-border rounded-lg p-3 bg-secondary/30 shrink-0">
                  <img src={`${API}/tickets/${a.ticket_id}/qr`} alt="ticket qr" className="w-40 h-40" data-testid={`ticket-qr-${a.ticket_id}`} />
                  <div className="text-[10px] font-mono text-stone-500">Scan at entry</div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-4 rounded-lg border border-dashed border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-stone-900/50 text-stone-400 shrink-0 w-36 h-36 text-center text-xs">
                  <AlertCircle size={28} className="text-stone-400 mb-2 opacity-50" />
                  <span className="font-medium text-[11px]">QR Locked</span>
                  <span className="text-[9px] text-stone-500 mt-0.5">Unpaid Booking</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {confirmed && (
          <p className="text-xs text-stone-500 mt-8">
            A confirmation email has been queued to {b.purchaser_email}. If SMTP is not configured, view delivery status in the admin panel.
          </p>
        )}
      </div>
    </PublicLayout>
  );
}